import React from 'react';
import { IGNORED_FIELDS, normalizeTimeValue } from '../helpers';
import { FieldLabel } from '../displays';

// Note: This component needs FieldDiff which creates a circular dependency.
// We solve this by accepting FieldDiff as a render prop or by lazy importing.
// For now, we'll import it dynamically to avoid the circular dependency at module load time.

export default function ObjectDiff({
  beforeValue,
  afterValue,
  depth = 0,
  attachmentLabels,
  labels,
  FieldDiffComponent,
  ignoreRecordId = false,
}) {
  const before = beforeValue ?? {};
  const after = afterValue ?? {};

  if (JSON.stringify(before) === JSON.stringify(after)) {
    return null;
  }

  const allKeys = [...new Set([...Object.keys(before), ...Object.keys(after)])];

  if (depth > 3) {
    return <span className="text-gray-400 text-sm">[nested object]</span>;
  }

  const changedEntries = allKeys
    .map((key) => {
      // Filter out ignored fields
      if (IGNORED_FIELDS.includes(key)) {
        return null;
      }

      // Ignore 'id' field when ignoreRecordId is true (for experience/hotel-matched items)
      if (ignoreRecordId && key === 'id') {
        return null;
      }

      const beforeVal = before[key];
      const afterVal = after[key];

      // For time fields, normalize before comparing
      if (key === 'arrivalTime' || key === 'departureTime') {
        const normalizedBefore = normalizeTimeValue(beforeVal);
        const normalizedAfter = normalizeTimeValue(afterVal);
        if (normalizedBefore === normalizedAfter) {
          return null;
        }
      }

      const hasChanged = JSON.stringify(beforeVal) !== JSON.stringify(afterVal);

      if (!hasChanged) return null;

      // Use the passed FieldDiffComponent if available, otherwise render basic diff
      if (FieldDiffComponent) {
        return (
          <div key={key}>
            <FieldLabel name={key} />
            <FieldDiffComponent
              fieldName={key}
              beforeValue={beforeVal}
              afterValue={afterVal}
              depth={depth + 1}
              attachmentLabels={attachmentLabels}
              labels={labels}
            />
          </div>
        );
      }

      // Fallback: simple display without recursive FieldDiff
      return (
        <div key={key}>
          <FieldLabel name={key} />
          <span className="text-xs text-gray-500">
            {JSON.stringify(beforeVal)} → {JSON.stringify(afterVal)}
          </span>
        </div>
      );
    })
    .filter(Boolean);

  if (changedEntries.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2 pl-2 border-l-2 border-gray-200">
      {changedEntries}
    </div>
  );
}
